import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, Table, Badge, Button, Spinner, Alert, Form, Row, Col, Pagination, Modal } from 'react-bootstrap';
import { FaCalendarCheck, FaCheckCircle, FaTimes, FaTrash } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const OnlineAppointments = () => {
  const { user } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [currentPage, setCurrentPage] = useState(1);
  const [appointmentsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);

  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;

  useEffect(() => {
    fetchOnlineAppointments();
  }, []);

  useEffect(() => {
    filterAppointments();
  }, [appointments, startDate, endDate]);

  const fetchOnlineAppointments = async () => {
    try {
      const res = await axios.get('/api/online-appointments.php');
      setAppointments(res.data);
      setFilteredAppointments(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to load online appointments');
      setLoading(false);
    }
  };

  const filterAppointments = () => {
    let filtered = [...appointments];

    if (startDate) {
      filtered = filtered.filter(app => {
        const appDate = new Date(app.createdAt).toISOString().split('T')[0];
        return appDate >= startDate;
      });
    }

    if (endDate) {
      filtered = filtered.filter(app => {
        const appDate = new Date(app.createdAt).toISOString().split('T')[0];
        return appDate <= endDate;
      });
    }

    setFilteredAppointments(filtered);
    setCurrentPage(1);
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`/api/online-appointments.php`, { id, action: 'approve' });
      fetchOnlineAppointments();
    } catch (err) {
      console.error(err);
      alert('Failed to approve appointment');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`/api/online-appointments.php`, { id, action: 'reject' });
      fetchOnlineAppointments();
    } catch (err) {
      console.error(err);
      alert('Failed to reject appointment');
    }
  };

  const handleDeleteClick = (appointment) => {
    setAppointmentToDelete(appointment);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!appointmentToDelete) return;
    
    try {
      await axios.delete(`/api/online-appointments.php?id=${appointmentToDelete.id}`);
      setAppointments(appointments.filter(a => a.id !== appointmentToDelete.id));
      setShowDeleteModal(false);
      setAppointmentToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Failed to delete appointment');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'approved':
        return <Badge bg="success">Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Pagination
  const indexOfLastAppointment = currentPage * appointmentsPerPage;
  const indexOfFirstAppointment = indexOfLastAppointment - appointmentsPerPage;
  const currentAppointments = filteredAppointments.slice(indexOfFirstAppointment, indexOfLastAppointment);
  const totalPages = Math.ceil(filteredAppointments.length / appointmentsPerPage);

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><FaCalendarCheck className="me-2" />Online Appointments</h2>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Header>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
                className="w-100"
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Header>
      </Card>

      <Card>
        <Card.Body>
          {filteredAppointments.length === 0 ? (
            <div className="text-center py-4">
              <p>No online appointments found.</p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Blood Group</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Doctor</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentAppointments.map((appointment) => (
                    <tr key={appointment.id}>
                      <td>{new Date(appointment.createdAt).toLocaleDateString('en-GB')}</td>
                      <td>{appointment.patientName}</td>
                      <td>{appointment.age || '-'}</td>
                      <td>{appointment.gender || '-'}</td>
                      <td>{appointment.bloodGroup || '-'}</td>
                      <td>{appointment.email}</td>
                      <td>{appointment.phone}</td>
                      <td>{appointment.doctorName || 'Any'}</td>
                      <td>{appointment.message || '-'}</td>
                      <td>{getStatusBadge(appointment.status)}</td>
                      <td>
                        {appointment.status === 'pending' && (
                          <>
                            {hasPermission('approveOnlineAppointment') && (
                              <Button
                                variant="success"
                                size="sm"
                                className="me-2"
                                onClick={() => handleApprove(appointment.id)}
                              >
                                <FaCheckCircle /> Approve
                              </Button>
                            )}
                            {hasPermission('rejectOnlineAppointment') && (
                              <Button
                                variant="danger"
                                size="sm"
                                className="me-2"
                                onClick={() => handleReject(appointment.id)}
                              >
                                <FaTimes /> Reject
                              </Button>
                            )}
                          </>
                        )}
                        {hasPermission('deleteOnlineAppointment') && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClick(appointment)}
                            title="Delete"
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    />
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this online appointment for{' '}
          <strong>{appointmentToDelete?.patientName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OnlineAppointments;
